// KundaliReportPage.js — Full Kundali paid PDF report viewer
// Accessed via: vedsanga.com/?kundali_id=xxx&kundali_token=yyy
import React, { useState, useEffect } from "react";
import { jsPDF } from "jspdf";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

// Matches JyotishPage.js's JT token system — white, one accent, plain Inter.
const RT = {
  page:     "#ffffff",
  card:     "#ffffff",
  border:   "#e2e2e2",
  shadow:   "0 1px 3px rgba(0,0,0,0.08)",
  font:     "'Inter',sans-serif",
  text:     "#1a1a1a",
  textDim:  "#5a5a5a",
  accent:   "#c2560f",
  accentDeep: "#9a4508",
};

const pdfSafe = s => String(s)
  .replace(/—|–/g, "-").replace(/•|·/g, "-")
  .replace(/['']/g, "'").replace(/[""]/g, '"')
  .replace(/[^\x00-\xFF]/g, "").trim();

function downloadKundaliPDF(report) {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const W = 210, M = 15, CW = W - 2 * M;
  let y = 0;
  const newPage = () => { doc.addPage(); y = 22; };
  const checkY  = (need = 10) => { if (y + need > 278) newPage(); };

  // Header — accent bar matching the site's single accent color
  doc.setFillColor(194, 86, 15);
  doc.rect(0, 0, W, 14, "F");
  doc.setFont("helvetica", "bold"); doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text("VedSanga  -  Vedic Astrology & Jyotish", M, 9);
  doc.text("Complete Kundali Reading", W - M, 9, { align: "right" });

  y = 22;
  doc.setFont("helvetica", "bold"); doc.setFontSize(20);
  doc.setTextColor(26, 26, 26);
  doc.text("Your Complete Kundali", M, y); y += 9;

  doc.setDrawColor(226, 226, 226); doc.setLineWidth(0.6);
  doc.line(M, y, W - M, y); y += 7;

  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  const meta = [
    `Name: ${pdfSafe(report.name || "")}   |   Moon Rashi: ${pdfSafe(report.moon_rashi || "")}   |   Nakshatra: ${pdfSafe(report.nakshatra || "")}`,
    `Lagna: ${pdfSafe(report.lagna_rashi || "Not provided")}   |   Maha Dasha: ${pdfSafe(report.maha_dasha || "")}`,
    `Born: ${pdfSafe(report.dob || "")}${report.birth_city ? "  |  " + pdfSafe(report.birth_city) : ""}`,
    `Generated: ${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}`,
  ];
  meta.forEach(l => { doc.text(pdfSafe(l), M, y); y += 5.5; });
  y += 3;
  doc.setDrawColor(226, 226, 226); doc.setLineWidth(0.3);
  doc.line(M, y, W - M, y); y += 9;

  (report.sections || []).forEach(sec => {
    checkY(22);
    doc.setFillColor(194, 86, 15);
    doc.roundedRect(M, y - 6, CW, 10, 2, 2, "F");
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    doc.text(pdfSafe(sec.title || sec.heading || ""), M + 4, y); y += 8;

    const body = sec.body || (sec.lines || []).join("\n");
    const paragraphs = body.split(/\n\n+/);
    paragraphs.forEach(para => {
      const trimmed = para.trim();
      if (!trimmed) return;
      checkY(10);
      doc.setFont("helvetica", "normal"); doc.setFontSize(10.5);
      doc.setTextColor(26, 26, 26);
      const wrapped = doc.splitTextToSize(pdfSafe(trimmed), CW - 2);
      wrapped.forEach(wl => { checkY(7); doc.text(wl, M + 2, y); y += 5.8; });
      y += 3;
    });
    y += 4;
  });

  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(120, 120, 120);
    doc.text("VedSanga - For spiritual & educational guidance only. Consult a qualified Jyotishi for important decisions.", M, 291);
    doc.text(`Page ${i} / ${total}`, W - M, 291, { align: "right" });
  }

  doc.save(`VedSanga_Kundali_${pdfSafe(report.name || "Report")}.pdf`);
}

function SectionCard({ sec }) {
  const [open, setOpen] = useState(true);
  const title = sec.title || sec.heading || "";
  const body  = sec.body  || (sec.lines || []).join("\n");
  return (
    <div style={{ marginBottom: 14, borderRadius: 14, overflow: "hidden",
      border: `1px solid ${RT.border}`, background: RT.card, boxShadow: RT.shadow }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width: "100%", textAlign: "left", padding: "14px 18px",
          background: "#fafafa",
          border: "none", borderBottom: open ? `1px solid ${RT.border}` : "none", cursor: "pointer", display: "flex",
          justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontFamily: RT.font, fontSize: "1.05rem",
          color: RT.text, fontWeight: 700 }}>{title}</span>
        <span style={{ color: RT.accent, fontSize: "1.1rem" }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div style={{ padding: "12px 18px 18px" }}>
          {body.split(/\n\n+/).map((para, i) => para.trim() ? (
            <p key={i} style={{ fontFamily: RT.font, fontSize: "1.05rem",
              color: RT.text, lineHeight: 1.75, marginBottom: "12px" }}>
              {para.trim()}
            </p>
          ) : null)}
        </div>
      )}
    </div>
  );
}

export default function KundaliReportPage({ kundaliId, token }) {
  const [report,  setReport]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    if (!kundaliId) return;
    fetch(`${API}/jyotish/kundali/full/report/${kundaliId}?token=${encodeURIComponent(token || "")}`)
      .then(r => {
        if (r.status === 402) throw new Error("Payment not confirmed yet. Please wait and refresh.");
        if (r.status === 403) throw new Error("Invalid link. Please use the exact link sent to your WhatsApp.");
        if (r.status === 404) throw new Error("Report not found. Please contact support.");
        return r.json();
      })
      .then(d => { setReport(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [kundaliId, token]);

  if (loading) return (
    <div style={{ minHeight: "100vh", background: RT.page, display: "flex",
      alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
      <div style={{ fontSize: "2.5rem", animation: "float 2s infinite ease-in-out" }}>🌟</div>
      <div style={{ color: RT.text, fontFamily: RT.font, fontSize: "1.05rem" }}>
        Loading your Kundali report...
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: "100vh", background: RT.page, display: "flex",
      alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ textAlign: "center", color: "#b91c1c", maxWidth: 340 }}>
        <div style={{ fontSize: "2rem", marginBottom: 12 }}>⚠️</div>
        <div style={{ fontFamily: RT.font, fontSize: "1.1rem", lineHeight: 1.6 }}>
          {error}
        </div>
      </div>
    </div>
  );

  if (!report) return null;

  return (
    <div style={{ minHeight: "100vh", background: RT.page, padding: "0 0 60px" }}>
      {/* Header */}
      <div style={{ background: RT.card, borderBottom: `1px solid ${RT.border}`,
        padding: "24px 20px 20px", textAlign: "center" }}>
        <div style={{ height: 3, width: 64, background: RT.accent, borderRadius: 2, margin: "0 auto 16px" }} />
        <div style={{ fontSize: "2rem", marginBottom: 8 }}>🌟</div>
        <div style={{ fontFamily: RT.font, fontSize: "1.4rem", fontWeight: 700,
          color: RT.text, marginBottom: 6 }}>
          Your Complete Kundali Reading
        </div>
        <div style={{ color: RT.textDim, fontSize: "1.05rem" }}>
          {report.name} · {report.moon_rashi} · {report.nakshatra}
        </div>
      </div>

      {/* Meta strip */}
      <div style={{ background: "#fafafa", borderBottom: `1px solid ${RT.border}`,
        padding: "12px 20px", display: "flex", gap: 16, flexWrap: "wrap",
        justifyContent: "center", fontSize: "1.0rem", color: RT.textDim }}>
        {report.lagna_rashi && <span>Lagna: <b style={{ color: RT.text }}>{report.lagna_rashi}</b></span>}
        {report.maha_dasha  && <span>Maha Dasha: <b style={{ color: RT.text }}>{report.maha_dasha}</b></span>}
        {report.dob         && <span>Born: <b style={{ color: RT.text }}>{report.dob}</b></span>}
        {report.birth_city  && <span>📍 {report.birth_city}</span>}
      </div>

      {/* Download button */}
      <div style={{ textAlign: "center", padding: "20px 20px 8px" }}>
        <button onClick={() => downloadKundaliPDF(report)}
          style={{ background: RT.accent,
            border: "none", borderRadius: 14, padding: "13px 32px",
            color: "#ffffff", fontFamily: RT.font, fontWeight: 700, fontSize: "1.05rem",
            cursor: "pointer", boxShadow: "0 4px 20px rgba(194,86,15,0.35)" }}>
          📥 Download PDF Report
        </button>
      </div>

      {/* Sections */}
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "8px 16px" }}>
        {(report.sections || []).map((sec, i) => (
          <SectionCard key={i} sec={sec} />
        ))}
      </div>

      <div style={{ textAlign: "center", padding: "20px", color: RT.textDim,
        fontSize: "1.0rem", fontFamily: RT.font }}>
        For spiritual & educational guidance only.<br />
        Consult a qualified Jyotishi for important life decisions.<br />
        — VedSanga | vedsanga.com
      </div>
    </div>
  );
}
